import { useEffect } from "react";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { PatternFormat } from "react-number-format";
import { useAppDispatch, useAppSelector } from "../store";
import { CartaoCredito } from "../types/assinatura";
import assinaturaApi from "../api/assinatura.api";
import clienteApi from "../api/cliente.api";
import cartaoCreditoSchema from "../schemas/cartaoCredito";
import useTitle from "../hooks/useTitle";
import BandeirasCartoes from "../components/BandeirasCartoes";
import Section from "../components/Section";
import SectionSkeleton from "../components/SectionSkeleton";
import enqueueSnackbar from "../helpers/enqueueSnackbar";
import { toCurrency } from "../helpers/toCurrency";

export default function AtualizarPagamentoPage() {
  useTitle("Atualizar pagamento");

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { loading, cliente, plano, assinatura } = useAppSelector(
    (state) => state.cliente
  );

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { isSubmitting, isSubmitSuccessful },
  } = useForm<CartaoCredito>({
    resolver: yupResolver(cartaoCreditoSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  async function load(abortController?: AbortController) {
    try {
      dispatch({ type: "SET_LOADING_CLIENTE", payload: true });

      const cliente = await clienteApi.load(
        {
          assinatura: true,
          plano: true,
        },
        abortController
      );

      if (cliente.tipoConta !== "C") {
      }

      if (!cliente?.plano || !cliente?.assinatura) {
        throw new Error(
          "Não foi possível obter as informações da sua assinatura."
        );
      }

      dispatch({ type: "SET_CLIENTE_PLANO", payload: cliente.plano });
      dispatch({
        type: "SET_CLIENTE_ASSINATURA",
        payload: cliente.assinatura,
      });
    } catch (error) {
      if (error?.code === "ERR_CANCELED") {
        return;
      }

      enqueueSnackbar(
        error?.message ||
          "Não foi possível obter as informações da sua assinatura.",
        "error"
      );
    } finally {
      dispatch({ type: "SET_LOADING_CLIENTE", payload: false });
    }
  }

  async function submit(data: CartaoCredito) {
    try {
      const validade = DateTime.fromFormat(data.validade, "MMyy")
        .startOf("month")
        .toISODate();

      await assinaturaApi.updateCartaoCredito({
        numero: data.numero,
        validade: validade,
        titular: data.titular,
        cvv: data.cvv,
      });

      enqueueSnackbar(
        "Os seus dados de pagamento foram atualizados.",
        "success"
      );

      navigate("/app/minha-conta");
    } catch (error) {
      enqueueSnackbar(
        error?.message ||
          "Não foi possível atualizar os seus dados de pagemento.",
        "error"
      );

      setError("root", error?.message);
    }
  }

  useEffect(() => {
    const abortController = new AbortController();

    if (!loading) {
      load(abortController);
    }

    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexFlow: "column nowrap",
        alignItems: "center",
        gap: theme.spacing(2),
        height: "100%",
      }}
    >
      <Box sx={{ textAlign: "center", pb: 2 }}>
        <Typography variant="h4">Atualizar pagamento</Typography>
        <Typography>
          Aqui você pode atualizar a forma de pagamento da sua assinatura.
        </Typography>
      </Box>

      {loading || !cliente || !plano || !assinatura ? (
        <SectionSkeleton />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexFlow: "column nowrap",
            gap: theme.spacing(2),
            [theme.breakpoints.up("lg")]: {
              flexFlow: "row nowrap",
              alignItems: "flex-start",
            },
          }}
        >
          <Section
            sx={{
              display: "flex",
              flexFlow: "column nowrap",
              gap: theme.spacing(2),
            }}
          >
            <div>
              <Typography variant="h6">Plano</Typography>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {plano?.nome}
              </Typography>
            </div>

            {!!cliente &&
              !!assinatura &&
              cliente.tipoConta === "C" &&
              assinatura.getnetSubscriptionId && (
                <div>
                  <Typography variant="h6">Forma de pagamento</Typography>
                  <Typography>
                    Cartão de crédito
                    {(!!assinatura?.bandeiraCartao ||
                      !!assinatura?.finalCartao) &&
                      `:${
                        assinatura?.bandeiraCartao
                          ? ` ${assinatura.bandeiraCartao}`
                          : ""
                      }${
                        assinatura?.finalCartao
                          ? ` com final ${assinatura.finalCartao}`
                          : ""
                      }`}
                  </Typography>
                  <Typography>
                    Próxima cobrança:{" "}
                    {DateTime.fromISO(
                      assinatura.dataProximoPagamento
                    ).toLocaleString(DateTime.DATE_FULL, { locale: "pt-br" })}
                  </Typography>
                  {!!plano && (
                    <Typography>
                      Valor: {toCurrency(plano.valor)} por mês
                    </Typography>
                  )}
                </div>
              )}

            <Box
              sx={{
                display: "flex",
                flexFlow: "column nowrap",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: theme.spacing(2),
                width: "100%",
                [theme.breakpoints.up("lg")]: {
                  flexFlow: "row wrap",
                  justifyContent: "center",
                  alignItems: "flex-start",
                },
              }}
            >
              <form onSubmit={handleSubmit(submit)}>
                <Box
                  sx={{
                    display: "flex",
                    flexFlow: "column nowrap",
                    gap: theme.spacing(2),
                  }}
                >
                  <Typography variant="h6">Dados do novo cartão</Typography>

                  <Controller
                    control={control}
                    name="numero"
                    render={({
                      field: { onBlur, value, name },
                      fieldState: { error },
                    }) => (
                      <PatternFormat
                        type="text"
                        label="Número do cartão"
                        name={name}
                        value={value || ""}
                        onBlur={onBlur}
                        onValueChange={({ value }) =>
                          setValue(name, value, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                          })
                        }
                        error={!!error}
                        helperText={!!error ? error.message : undefined}
                        placeholder="1111 2222 3333 4444"
                        format="#### #### #### ####"
                        mask="_"
                        valueIsNumericString
                        customInput={TextField}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="titular"
                    render={({
                      field: { onBlur, onChange, value, name },
                      fieldState: { error },
                    }) => (
                      <TextField
                        type="text"
                        variant="outlined"
                        label="Nome impresso no cartão"
                        placeholder="Nome impresso no cartão"
                        name={name}
                        value={value || ""}
                        onBlur={onBlur}
                        onChange={onChange}
                        error={!!error}
                        helperText={!!error ? error.message : undefined}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ notched: true }}
                        fullWidth
                      />
                    )}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      flexflow: "row wrap",
                      gap: theme.spacing(2),
                    }}
                  >
                    <Controller
                      control={control}
                      name="validade"
                      render={({
                        field: { onBlur, value, name },
                        fieldState: { error },
                      }) => (
                        <PatternFormat
                          type="text"
                          label="Validade do cartão"
                          placeholder={`${DateTime.now().toFormat("LL / yy")}`}
                          name={name}
                          value={value || ""}
                          onBlur={onBlur}
                          onValueChange={({ value }) =>
                            setValue(name, value, {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            })
                          }
                          error={!!error}
                          helperText={!!error ? error.message : undefined}
                          format="## / ##"
                          mask="_"
                          valueIsNumericString
                          customInput={TextField}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name="cvv"
                      render={({
                        field: { onBlur, value, name },
                        fieldState: { error },
                      }) => (
                        <PatternFormat
                          type="text"
                          label="CVV (Código de segurança)"
                          placeholder="123"
                          name={name}
                          value={value || ""}
                          onBlur={onBlur}
                          onValueChange={({ value }) =>
                            setValue(name, value, {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            })
                          }
                          error={!!error}
                          helperText={!!error ? error.message : undefined}
                          format="###"
                          mask="_"
                          valueIsNumericString
                          customInput={TextField}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                        />
                      )}
                    />
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    type="submit"
                    disabled={isSubmitting || isSubmitSuccessful}
                    endIcon={isSubmitting ? <CircularProgress /> : undefined}
                  >
                    Atualizar cartão
                  </Button>

                  <BandeirasCartoes imgHeight={56} />
                </Box>
              </form>
            </Box>
          </Section>
        </Box>
      )}
    </Box>
  );
}
